import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CalendarClock } from 'lucide-react';
import ModuleTabHeader, { ModuleTab } from '../../components/common/ModuleTabHeader';
import CadenceInsights from '../../components/cadence/CadenceInsights';
import CadenceRuleList from '../../components/cadence/CadenceRuleList';
import CadenceEnginePage from '../../components/cadence/CadenceEnginePage';
import CadenceTentativeTab from '../../components/cadence/CadenceTentativeTab';
import CadenceFormedUpTab from '../../components/cadence/CadenceFormedUpTab';
import '../../components/layout/ModuleLayout.css';

type Tab = 'insights' | 'rules' | 'tentative' | 'formed' | 'engine';

const cadenceTabs: ModuleTab[] = [
  { key: 'insights',  label: 'Insights'   },
  { key: 'rules',     label: 'Rules'      },
  { key: 'tentative', label: 'Tentative'  },
  { key: 'formed',    label: 'Formed-up'  },
  { key: 'engine',    label: 'Engine'     },
];

const VALID_TABS: Tab[] = ['insights', 'rules', 'tentative', 'formed', 'engine'];

const CadenceView: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') || '';
  const initialTab: Tab = (VALID_TABS as string[]).includes(rawTab) ? (rawTab as Tab) : 'insights';

  const [tab, setTab] = useState<Tab>(initialTab);

  // Sync the active tab when ?tab=… changes after mount (e.g. the global header
  // search navigates here while Cadence is already open — the URL changes but
  // this view does not remount, so useState(initialTab) alone would be stale).
  useEffect(() => {
    if ((VALID_TABS as string[]).includes(rawTab) && rawTab !== tab) {
      setTab(rawTab as Tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTab]);

  const changeTab = (key: Tab) => {
    setTab(key);
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: false });
  };

  return (
    <div className="module-page">
      <ModuleTabHeader
        title="Cadence"
        icon={<CalendarClock size={16} strokeWidth={1.75} />}
        tabs={cadenceTabs}
        activeTab={tab}
        onTabChange={(k) => changeTab(k as Tab)}
      />

      <div className="module-layout">
        <div className="module-main">
          <div className="tab-content">
            <section role="tabpanel" className="tab-panel show">
              {tab === 'insights' && <CadenceInsights />}
              {tab === 'rules' && <CadenceRuleList />}
              {tab === 'tentative' && <CadenceTentativeTab />}
              {tab === 'formed' && <CadenceFormedUpTab />}
              {tab === 'engine' && <CadenceEnginePage />}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadenceView;
